"use client";

import { Languages } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PSEUDO, UI_LANGUAGES, languagesEnabled } from "@/lib/i18n";
import { usePortalLanguage } from "@/lib/i18n/usePortalLanguage";

// Portal language picker: each language in its own name, never a flag (a flag
// is a country, not a language). Hidden until languages are enabled (lib/i18n).
export default function LanguageSwitcher({ className = "" }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const { choose } = usePortalLanguage();
  if (!languagesEnabled()) return null;

  const host = window.location.hostname;
  const showPseudo = host === "localhost" || host === "127.0.0.1" || host.startsWith("staging.");
  const options = showPseudo ? [...UI_LANGUAGES, { code: PSEUDO, name: "Pseudo (layout test)", intl: "en-US" }] : UI_LANGUAGES;
  const current = options.find((l) => l.code === i18n.language) || UI_LANGUAGES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={`flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-sm text-foreground hover:bg-accent ${className}`}
        aria-label={t("Language")}
        data-analytics-id="portal_language_menu"
      >
        <Languages className="h-4 w-4" />
        <span className="hidden sm:inline">{current.name}</span>
        <span className="sm:hidden uppercase">{current.code.slice(0, 2)}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        <DropdownMenuRadioGroup value={current.code} onValueChange={(code) => void choose(code)}>
          {options.map((l) => (
            <DropdownMenuRadioItem key={l.code} value={l.code} lang={l.code === PSEUDO ? "en" : l.code}>
              {l.name}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
